import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import { CloudUpload, Calculate, Edit } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useNavigate, useLocation } from 'react-router-dom';
import { uploadService } from '../services/uploadService';
import { predictionService } from '../services/predictionService';
import { UploadedFile, Prediction } from '../types';
import { CashFlowTable } from '../components/prediction/CashFlowTable';
import { CashFlowChart } from '../components/prediction/CashFlowChart';
import { Header } from '../components/layout/Header';

export const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedFile, setSelectedFile] = useState<UploadedFile | null>(null);
  const [uploads, setUploads] = useState<UploadedFile[]>([]);
  const [modelName, setModelName] = useState<'linear' | 'random' | 'random_forest_api_v6_1' | 'random_forest_quantile_api_v6_1' | 'lightgbm_api_v7_1' | 'random_forest_quantile_api_v7_1'>('linear');
  const [numTrainingSamples, setNumTrainingSamples] = useState<number | ''>('');
  const [purchasePrice, setPurchasePrice] = useState<number | ''>('');
  const [interestRate, setInterestRate] = useState<number | ''>(6);
  const [targetMonths, setTargetMonths] = useState<number | ''>('');
  const [forecastHorizonDays, setForecastHorizonDays] = useState<number | ''>('');
  const [forecastIntervalDays, setForecastIntervalDays] = useState<number | ''>('');
  const [predictRanges, setPredictRanges] = useState<string>('');
  const [confidenceIntervalPercent, setConfidenceIntervalPercent] = useState<number | ''>('');
  const [prediction, setPrediction] = useState<Prediction | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    loadUploads();
  }, []);

  useEffect(() => {
    // If uploadId is provided via location state, select it automatically
    if (location.state?.uploadId && uploads.length > 0) {
      const file = uploads.find(u => u.id === location.state.uploadId);
      if (file) {
        setSelectedFile(file);
      }
    }
  }, [location.state, uploads]);

  const loadUploads = async () => {
    try {
      const data = await uploadService.getUploads();
      setUploads(data);
    } catch (err) {
      console.error('Failed to load uploads:', err);
    }
  };

  const onDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setError('');
    setLoading(true);

    try {
      const uploadedFile = await uploadService.uploadFile(file);
      setSelectedFile(uploadedFile);
      await loadUploads();

      // Redirect to mapping editor
      navigate(`/mapping-editor?uploadId=${uploadedFile.id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fájl feltöltési hiba');
    } finally {
      setLoading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
  });

  const handleRunPrediction = async () => {
    if (!selectedFile) {
      setError('Kérem válasszon ki egy fájlt');
      return;
    }

    setError('');
    setLoading(true);
    setPrediction({ status: 'PENDING' } as any); // Show loading box immediately

    try {
      const request = {
        uploadId: selectedFile.id,
        modelName,
        numTrainingSamples:
          modelName === 'random_forest_api_v6_1' && numTrainingSamples !== ''
            ? Number(numTrainingSamples)
            : undefined,
        purchasePrice: purchasePrice !== '' ? Number(purchasePrice) : undefined,
        interestRate: interestRate !== '' ? Number(interestRate) : undefined,
        targetMonths: targetMonths !== '' ? Number(targetMonths) : undefined,
        forecastHorizonDays:
          (modelName === 'lightgbm_api_v7_1' || modelName === 'random_forest_quantile_api_v7_1') && forecastHorizonDays !== ''
            ? Number(forecastHorizonDays)
            : undefined,
        forecastIntervalDays:
          (modelName === 'lightgbm_api_v7_1' || modelName === 'random_forest_quantile_api_v7_1') && forecastIntervalDays !== ''
            ? Number(forecastIntervalDays)
            : undefined,
        predictRanges:
          modelName === 'random_forest_quantile_api_v7_1' && predictRanges !== ''
            ? predictRanges
            : undefined,
        confidenceIntervalPercent:
          modelName === 'random_forest_quantile_api_v7_1' && confidenceIntervalPercent !== ''
            ? Number(confidenceIntervalPercent)
            : undefined,
      };

      const result = await predictionService.createPrediction(request);
      setPrediction(result);

      // Poll for completion
      pollPredictionStatus(result.id);
    } catch (err: any) {
      console.error('Failed to create prediction:', err);
      console.error('Error details:', {
        response: err.response,
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });

      let errorMessage = 'Előrejelzési hiba';

      if (err.response?.data) {
        // Backend returned an error message
        errorMessage = err.response.data.error ||
                      err.response.data.message ||
                      errorMessage;

        // Add validation errors if present
        if (err.response.data.validationErrors) {
          const validationErrors = Object.values(err.response.data.validationErrors).join(', ');
          errorMessage += ` (${validationErrors})`;
        }
      } else if (err.response) {
        // Server responded with error but no data
        errorMessage = `Hiba (${err.response.status}): ${err.response.statusText}`;
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'Nem sikerült kapcsolódni a szerverhez. Kérjük, ellenőrizze az internetkapcsolatot.';
      } else {
        // Something else happened
        errorMessage = err.message || errorMessage;
      }

      setError(errorMessage);
      setLoading(false);
    }
  };

  const pollPredictionStatus = async (predictionId: number) => {
    const maxAttempts = 60; // 5 minutes max
    let attempts = 0;

    const poll = async () => {
      try {
        const updated = await predictionService.getPrediction(predictionId);
        setPrediction(updated);

        if (updated.status === 'COMPLETED') {
          setLoading(false);
          return;
        }

        if (updated.status === 'FAILED') {
          setError(updated.errorMessage || 'Előrejelzés sikertelen');
          setLoading(false);
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000); // Poll every 5 seconds
        } else {
          setError('Időtúllépés - az előrejelzés túl sokáig tart');
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to poll prediction status:', err);
        setLoading(false);
      }
    };

    setTimeout(poll, 5000);
  };

  return (
    <>
      <Header />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Csomag Árazás
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Cash-flow előrejelzés Excel alapú követelés csomagra
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left Panel - Upload and Settings */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                1. Excel Feltöltés
              </Typography>

              <Box
                {...getRootProps()}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragActive ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                  mb: 2,
                }}
              >
                <input {...getInputProps()} />
                <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {isDragActive
                    ? 'Húzza ide a fájlt...'
                    : 'Kattintson vagy húzzon ide egy Excel fájlt'}
                </Typography>
              </Box>

              {selectedFile && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Kiválasztott fájl: {selectedFile.fileName}
                </Alert>
              )}

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Korábbi feltöltések</InputLabel>
                <Select
                  value={selectedFile?.id || ''}
                  onChange={(e) => {
                    const file = uploads.find((u) => u.id === e.target.value);
                    if (file) setSelectedFile(file);
                  }}
                  label="Korábbi feltöltések"
                >
                  {uploads.map((upload) => (
                    <MenuItem key={upload.id} value={upload.id}>
                      {upload.fileName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedFile && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  startIcon={<Edit />}
                  onClick={() => navigate(`/mapping-editor?uploadId=${selectedFile.id}`)}
                  sx={{ mb: 2 }}
                >
                  Mapping szerkesztő megnyitása
                </Button>
              )}
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                2. Paraméterek
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Modell</InputLabel>
                <Select
                  value={modelName}
                  onChange={(e) => setModelName(e.target.value as 'linear' | 'random' | 'random_forest_api_v6_1' | 'random_forest_quantile_api_v6_1' | 'lightgbm_api_v7_1' | 'random_forest_quantile_api_v7_1')}
                  label="Modell"
                >
                  <MenuItem value="linear">Linear Model</MenuItem>
                  <MenuItem value="random">Random Model</MenuItem>
                  <MenuItem value="random_forest_api_v6_1">Random Forest (v6)</MenuItem>
                  <MenuItem value="random_forest_quantile_api_v6_1">Random Forest Quantile (v6)</MenuItem>
                  <MenuItem value="lightgbm_api_v7_1">LightGBM V7 1</MenuItem>
                  <MenuItem value="random_forest_quantile_api_v7_1">Random Forest V7 1</MenuItem>
                </Select>
              </FormControl>

              {modelName === 'random_forest_api_v6_1' && (
                <TextField
                  fullWidth
                  type="number"
                  label="Training minták száma (opcionális)"
                  value={numTrainingSamples}
                  onChange={(e) =>
                    setNumTrainingSamples(e.target.value ? Number(e.target.value) : '')
                  }
                  sx={{ mb: 2 }}
                  helperText="Ha üres, akkor a modell az összes elérhető sort használja. Teszthez javasolt pl. 500–2000."
                />
              )}

              <TextField
                fullWidth
                type="number"
                label="Vételár (opcionális)"
                value={purchasePrice}
                onChange={(e) => setPurchasePrice(e.target.value ? Number(e.target.value) : '')}
                sx={{ mb: 2 }}
                helperText="HUF"
              />

              <TextField
                fullWidth
                type="number"
                label="Tőke kamatköltség (%)"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value ? Number(e.target.value) : '')}
                sx={{ mb: 2 }}
                helperText="Éves kamatköltség százalékban"
              />

              <TextField
                fullWidth
                type="number"
                label="Megtérülési idő (hónap, opcionális)"
                value={targetMonths}
                onChange={(e) => setTargetMonths(e.target.value ? Number(e.target.value) : '')}
                sx={{ mb: 2 }}
                helperText="Célzott megtérülési idő"
              />

              {modelName === 'lightgbm_api_v7_1' && (
                <>
                  <TextField
                    fullWidth
                    type="number"
                    label="Előrejelzési időtáv (nap)"
                    value={forecastHorizonDays}
                    onChange={(e) => setForecastHorizonDays(e.target.value ? Number(e.target.value) : '')}
                    sx={{ mb: 2 }}
                    helperText="Hány napra előre történjen az előrejelzés (pl. 1830 = 5 év)"
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Előrejelzési intervallum (nap)"
                    value={forecastIntervalDays}
                    onChange={(e) => setForecastIntervalDays(e.target.value ? Number(e.target.value) : '')}
                    sx={{ mb: 2 }}
                    helperText="Milyen gyakorisággal történjen az előrejelzés (pl. 30 = havonta)"
                  />
                </>
              )}

              {modelName === 'random_forest_quantile_api_v7_1' && (
                <>
                  <TextField
                    fullWidth
                    type="number"
                    label="Előrejelzési időtáv (nap)"
                    value={forecastHorizonDays}
                    onChange={(e) => setForecastHorizonDays(e.target.value ? Number(e.target.value) : '')}
                    sx={{ mb: 2 }}
                    helperText="Hány napra előre történjen az előrejelzés (pl. 1830 = 5 év)"
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Előrejelzési intervallum (nap)"
                    value={forecastIntervalDays}
                    onChange={(e) => setForecastIntervalDays(e.target.value ? Number(e.target.value) : '')}
                    sx={{ mb: 2 }}
                    helperText="Milyen gyakorisággal történjen az előrejelzés (pl. 30 = havonta)"
                  />
                  <TextField
                    fullWidth
                    type="text"
                    label="Előrejelzési tartományok (opcionális)"
                    value={predictRanges}
                    onChange={(e) => setPredictRanges(e.target.value)}
                    sx={{ mb: 2 }}
                    helperText="Pl. 0.1,0.2,0.3 (vesszővel elválasztva)"
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Bizonytalansági intervallum (%) (opcionális)"
                    value={confidenceIntervalPercent}
                    onChange={(e) => setConfidenceIntervalPercent(e.target.value ? Number(e.target.value) : '')}
                    sx={{ mb: 2 }}
                    helperText="Pl. 80, 90 (százalékban)"
                  />
                </>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={20} /> : <Calculate />}
                onClick={handleRunPrediction}
                disabled={!selectedFile || loading}
              >
                {loading ? 'Futtatás...' : 'Előrejelzés Futtatása'}
              </Button>
            </Paper>
          </Grid>

          {/* Right Panel - Results */}
          <Grid item xs={12} md={8}>
            {prediction && prediction.status === 'COMPLETED' && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Előrejelzés Eredmények
                </Typography>

                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
                  <Tab label="Grafikon" />
                  <Tab label="Táblázat" />
                </Tabs>

                {tabValue === 0 && (
                  <CashFlowChart
                    prediction={prediction}
                    interestRate={typeof interestRate === 'number' ? interestRate : undefined}
                    purchasePrice={typeof purchasePrice === 'number' ? purchasePrice : undefined}
                    intervalDays={typeof prediction.forecastIntervalDays === 'number' ? prediction.forecastIntervalDays : 30}
                    startDate={prediction.results ? JSON.parse(prediction.results).startDate : ''}
                  />
                )}

                {tabValue === 1 && (
                  <CashFlowTable
                    prediction={prediction}
                    purchasePrice={purchasePrice !== '' ? Number(purchasePrice) : undefined}
                  />
                )}
              </Paper>
            )}

            {prediction && (prediction.status === 'PENDING' || prediction.status === 'RUNNING') && (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  Az előrejelzés futtatása folyamatban...
                </Typography>
                <Box sx={{ mt: 2, mb: 2 }}>
                  {/* Status description text area */}
                  {prediction.statusDescription && (
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        value={prediction.statusDescription}
                        multiline
                        minRows={2}
                        maxRows={6}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                        label="Állapot leírás"
                      />
                    </Box>
                  )}
                  {/* Progress bar or spinner logic */}
                  {typeof prediction.eta === 'number' && typeof prediction.timeSinceSubmission === 'number' ? (
                    <ProgressWithETA eta={prediction.eta} timeSinceSubmission={prediction.timeSinceSubmission} />
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 60 }}>
                      <CircularProgress />
                    </Box>
                  )}
                </Box>
              </Paper>
            )}

            {!prediction && (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  Töltsön fel egy Excel fájlt és futtassa az előrejelzést az eredmények megtekintéséhez
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

// ProgressWithETA component
const ProgressWithETA: React.FC<{ eta: number; timeSinceSubmission: number }> = ({ eta, timeSinceSubmission }) => {
  const [elapsed, setElapsed] = React.useState(timeSinceSubmission);
  React.useEffect(() => {
    setElapsed(timeSinceSubmission);
    if (typeof eta !== 'number') return;
    const interval = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [timeSinceSubmission, eta]);

  const total = eta + timeSinceSubmission;
  const remaining = Math.max(eta - (elapsed - timeSinceSubmission), 0);

  // Format remaining as MM:SS
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  const etaString = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

  return (
    <Box>
      <Box sx={{ width: '100%', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ flexGrow: 1 }}>
            <progress value={elapsed} max={total} style={{ width: '100%', height: 16 }} />
          </Box>
          <Box sx={{ ml: 2, minWidth: 60 }}>
            <Typography variant="body2" color="text.secondary">
              Hátralévő idő: {etaString}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
